import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { genericCommentApi } from '../api/comments';
import { ApiError } from '../api/client';
import { useToast } from './Toast';

/**
 * Reusable comment thread for any entity - reuses the exact same
 * comments table already proven for Projects, now genuinely
 * project-independent (migration 023).
 *
 * `section` (migration 049) matches the OneGMS reference exactly: a
 * record with multiple tabs gets a separate thread per tab ("Comments
 * for FNR Programming" only shows what was posted on the Programming
 * tab), rather than one thread for the whole record. Omit `section` for
 * a record with no natural tab split - it then behaves as before,
 * one whole-record thread. `heading` controls the visible title so
 * callers can label it the same way OneGMS does (e.g. "Comments for
 * Employment").
 */
export function CommentsThread({ entityType, entityId, section, heading = 'Comments' }: { entityType: string; entityId: number; section?: string; heading?: string }) {
  const { showError } = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const queryKey = ['comments', entityType, entityId, section ?? null];

  const commentsQuery = useQuery({ queryKey, queryFn: () => genericCommentApi.list(entityType, entityId, section) });

  const createMutation = useMutation({
    mutationFn: (text: string) => genericCommentApi.create(entityType, entityId, text, section),
    onSuccess: () => { setBody(''); queryClient.invalidateQueries({ queryKey }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to post comment.'),
  });

  return (
    <section className="card">
      <h2>{heading}</h2>
      {commentsQuery.data && (
        commentsQuery.data.data.length === 0 ? (
          <p className="muted">No comments yet.</p>
        ) : (
          <ul className="deadline-list">
            {commentsQuery.data.data.map((c) => (
              <li key={c.id}><strong>{c.author_name}</strong> <span className="muted">({new Date(c.created_at).toLocaleString()})</span><br />{c.body}</li>
            ))}
          </ul>
        )
      )}
      <div className="inline-form">
        <label style={{ flex: 1 }}>Add a comment<textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} /></label>
        <button onClick={() => { if (body.trim()) createMutation.mutate(body); else showError('Comment cannot be empty.'); }} disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Posting…' : 'Post'}
        </button>
      </div>
    </section>
  );
}
